import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { Order } from './KanbanCard'

interface StatusConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  order: Order | null
  newStatus: string | null
}

const STATUS_LABELS: Record<string, string> = {
  AGUARDANDO: 'Fila de Espera',
  LAVANDO: 'Lavando',
  FINALIZANDO: 'Secando/Finalizando',
  CONCLUIDO: 'Pronto para Entrega',
}

const STATUS_COLORS: Record<string, string> = {
  AGUARDANDO: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  LAVANDO: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
  FINALIZANDO: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
  CONCLUIDO: 'text-green-400 bg-green-500/10 border-green-500/20',
}

export function StatusConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  order,
  newStatus,
}: StatusConfirmModalProps) {
  if (!order || !newStatus) return null

  const currentLabel = STATUS_LABELS[order.status] || STATUS_LABELS.AGUARDANDO
  const newLabel = STATUS_LABELS[newStatus] || newStatus

  const currentColorClass = STATUS_COLORS[order.status] || STATUS_COLORS.AGUARDANDO
  const newColorClass = STATUS_COLORS[newStatus] || 'text-slate-300'

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl">Confirmar Alteração de Etapa</DialogTitle>
          <DialogDescription className="text-slate-400">
            Você está prestes a mover esta Ordem de Serviço.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 my-4 space-y-3">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-mono text-sm text-slate-400">OS #{order.id.slice(0, 8)}</div>
              <div className="font-bold text-lg">
                {order.vehicle_model}{' '}
                <span className="font-mono text-slate-300 ml-1">{order.plate}</span>
              </div>
              <div className="text-sm text-slate-400">{order.customer_name}</div>
            </div>
            <div className="text-right">
              <div className="text-emerald-400 font-bold">R$ {Number(order.total).toFixed(2)}</div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-slate-700">
            <div
              className={`px-3 py-1.5 rounded-md border text-xs font-bold uppercase ${currentColorClass}`}
            >
              {currentLabel}
            </div>
            <ArrowRight className="text-slate-500" size={16} />
            <div
              className={`px-3 py-1.5 rounded-md border text-xs font-bold uppercase ${newColorClass}`}
            >
              {newLabel}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-2">
          <Button variant="ghost" onClick={onClose} className="hover:bg-slate-800 text-slate-300">
            Cancelar
          </Button>
          <Button
            onClick={() => {
              onConfirm()
              onClose()
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold"
          >
            Confirmar Mudança
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
